import { PgProviderFeeItem } from '../../../lib/api/pgProviderFee';
import { EditableFee, EditableFeeField, FeeChange, ReverseMarginFee } from '../types';

/** 편집 컬럼과 화면 이름. 순서가 곧 확인 모달에 쌓이는 순서다. */
export const FEE_FIELDS: { key: EditableFeeField; label: string; unit: 'percent' | 'won' }[] = [
  { key: 'feeRate', label: '명목 요율', unit: 'percent' },
  { key: 'minimumFeeAmount', label: '최소 수수료', unit: 'won' },
  { key: 'minimumFeeRateAmount', label: '최소 요율 금액', unit: 'won' },
  { key: 'feeRateActual', label: '실제 요율', unit: 'percent' },
  { key: 'minimumFeeAmountActual', label: '실제 최소 수수료', unit: 'won' },
];

export const formatFeeValue = (value: number | null, unit: 'percent' | 'won'): string => {
  if (value === null || value === undefined) return '없음';
  return unit === 'percent' ? `${value.toFixed(2)}%` : `${value.toLocaleString()}원`;
};

/**
 * 편집 대상만 남긴 비교용 형태.
 *
 * 응답에는 `lastPaymentDate`·`isDormant` 처럼 서버가 매번 다시 계산하는 값이 섞여 있어,
 * 응답 전체를 비교하면 결제가 한 건 더 들어온 것만으로도 "원격 변경" 으로 잡힌다.
 */
export const toComparableFees = (list: EditableFee[]): EditableFeePayload[] => toFeePayload(list);

/** 편집 중에는 요율이 비어 있을 수 있어 전송 타입과 형태가 다르다. */
type EditableFeePayload = Omit<PgProviderFeeItem, 'feeRate' | 'feeRateActual'> & {
  feeRate: number | null;
  feeRateActual: number | null;
};

export const toFeePayload = (list: EditableFee[]): EditableFeePayload[] =>
  list.map((fee) => ({
    id: fee.id,
    feeRate: fee.feeRate,
    feeRateActual: fee.feeRateActual,
    minimumFeeAmount: fee.minimumFeeAmount,
    minimumFeeAmountActual: fee.minimumFeeAmountActual,
    minimumFeeRateAmount: fee.minimumFeeRateAmount,
  }));

/** 바뀐 행만 골라 보낸다 — 손대지 않은 행까지 보내면 이력에 남는 "변경"의 의미가 흐려진다. */
export const getChangedFees = (before: EditableFee[], after: EditableFee[]): PgProviderFeeItem[] => {
  const beforeById = new Map(before.map((fee) => [fee.id, fee]));

  return toFeePayload(
    after.filter((fee) => {
      const original = beforeById.get(fee.id);
      if (!original) return false;
      return FEE_FIELDS.some(({ key }) => fee[key] !== original[key]);
    }),
    // 요율이 빈 행은 저장 자체를 막지만(FeePanel 의 isInvalid), 타입으로도 흘러나가지 않게 한 번 더 거른다
  ).filter((fee): fee is PgProviderFeeItem => fee.feeRate !== null && fee.feeRateActual !== null);
};

export const getFeeChanges = (before: EditableFee[], after: EditableFee[]): FeeChange[] => {
  const beforeById = new Map(before.map((fee) => [fee.id, fee]));
  const changes: FeeChange[] = [];
  after.forEach((fee) => {
    const original = beforeById.get(fee.id);
    if (!original) return;
    FEE_FIELDS.forEach(({ key, label, unit }) => {
      if (fee[key] === original[key]) return;
      changes.push({
        label: `${fee.pgProviderKorName} · ${fee.payMethodName} — ${label}`,
        before: formatFeeValue(original[key], unit),
        after: formatFeeValue(fee[key], unit),
      });
    });
  });
  return changes;
};

/**
 * 명목 요율이 실제 요율보다 낮으면 우리가 손해다. 막지는 않고 저장 전에 경고한다.
 *
 * 🔴 요율이 비어 있는 행은 제외한다. `null < 2.33` 은 참이라 그냥 비교하면 빈 행이 역마진으로 잡히고,
 * 확인 모달이 그 행을 `toFixed()` 로 그리다 터진다(모달이 닫혀 있어도 JSX 는 평가된다).
 * 반환 타입을 좁혀 두면 모달 쪽에서 같은 실수를 tsc 가 잡는다.
 */
export const isReverseMargin = (fee: EditableFee): boolean =>
  fee.feeRate !== null && fee.feeRateActual !== null && fee.feeRate < fee.feeRateActual;

export const getReverseMarginFees = (list: EditableFee[]): ReverseMarginFee[] =>
  list.filter((fee): fee is ReverseMarginFee => isReverseMargin(fee));
